<!--
  Worked examples for the home page: the five situations that account for nearly every question
  a new annotator asks in their first hour.

  Rendered with **real Konva stages** driven by `lib/boxStyle.ts` — the same module
  `ImageCanvas.svelte` and `/styles` render from. That is the point of doing it this way rather
  than with CSS rectangles: an example drawn by a second, parallel set of style rules can drift
  from the tool and then teach something the tool does not do. Here it cannot.

  The glyph marks are **abstract placeholder shapes, deliberately not Hismaic letterforms.**
  This page teaches the workflow; drawing something that looked like a real `ṣ` would be
  inventing a glyph form, which is exactly what this project must never do. Real letterforms are
  learned from the stones and from `docs/annotation_guidelines.md`, not from a diagram somebody
  drew freehand. There is also no photograph here on purpose: a presigned S3 URL costs money,
  expires, and would make this page fail on an installation with no bucket configured.
-->
<script lang="ts">
  import Konva from 'konva';

  import type { TokenOut } from '../lib/api/types';
  import { LABEL_OFFSET, boxLabelAttrs, boxRectAttrs } from '../lib/boxStyle';
  import { rectToOriented, type Rect } from '../lib/coords';
  import type { DraftBox } from '../lib/draft';
  import { isLacunaToken, tokenLabel } from '../lib/draft';

  // -------------------------------------------------------------------------
  // Geometry
  // -------------------------------------------------------------------------

  /** One glyph slot on the synthetic stone, in image pixels. */
  const SLOT = { x0: 26, y: 46, w: 52, h: 60, pitch: 84 };
  const STAGE_HEIGHT = 132;
  /** How far outside the mark the box sits. A tight box, which is what §5.1 asks for. */
  const BOX_PAD = 5;

  function slot(index: number): Rect {
    return { x: SLOT.x0 + index * SLOT.pitch, y: SLOT.y, w: SLOT.w, h: SLOT.h };
  }

  function boxAround(index: number): Rect {
    const mark = slot(index);
    return {
      x: mark.x - BOX_PAD,
      y: mark.y - BOX_PAD,
      w: mark.w + BOX_PAD * 2,
      h: mark.h + BOX_PAD * 2,
    };
  }

  /**
   * Abstract marks — flat `[x, y, x, y, …]` polylines in a 0..1 box, cycled by slot index.
   *
   * Not letters. Not derived from any inscription. Fixed rather than random so the page renders
   * identically every time and a test can assert on it.
   */
  const MARKS: number[][][] = [
    [
      [0.5, 0, 0.5, 1],
      [0.18, 0.34, 0.82, 0.34],
    ],
    [[0.2, 0, 0.8, 0.5, 0.2, 1]],
    [
      [0.22, 0, 0.22, 1],
      [0.22, 0.46, 0.82, 0.12],
      [0.22, 0.46, 0.82, 0.86],
    ],
    [
      [0.14, 0.16, 0.86, 0.16],
      [0.5, 0.16, 0.5, 1],
    ],
    [[0.24, 0.05, 0.78, 0.05, 0.78, 0.62, 0.24, 0.62, 0.24, 0.05]],
  ];

  const ROCK = '#7c756a';
  const ROCK_SHADE = '#6d675d';
  const INCISION = '#33302b';

  // -------------------------------------------------------------------------
  // Examples
  // -------------------------------------------------------------------------

  /**
   * Vocabulary ids are the real ones from `vocab.json` (append-only, and a model's output-head
   * ordering). Nothing here depends on them — the chips render `display` — but using invented
   * numbers in a teaching page is the sort of thing that gets copied.
   */
  const CLASS = { l: 13, r: 17, ṯ: 23, b: 2, n: 15, ṣ: 20, t: 21, illegible: 28 } as const;

  function token(
    index: number,
    letter: string,
    display: string,
    letterClass: number,
    flags: { uncertain?: boolean; restored?: boolean; wordIndex?: number } = {},
  ): TokenOut {
    return {
      index,
      letter,
      display,
      letter_class: letterClass,
      raw: display,
      uncertain: flags.uncertain ?? false,
      restored: flags.restored ?? false,
      word_index: flags.wordIndex ?? 0,
    };
  }

  function box(
    slotIndex: number,
    letter: string,
    display: string,
    letterClass: number,
    overrides: Partial<DraftBox> = {},
  ): DraftBox {
    return {
      key: `example-${slotIndex}-${letter}`,
      id: null,
      letterClass,
      letter,
      display,
      bbox: rectToOriented(boxAround(slotIndex)),
      alignment: 'expected',
      expectedTokenIndex: slotIndex,
      wordIndex: 0,
      mirrored: false,
      chirality: null,
      uncertain: false,
      restored: false,
      source: 'human',
      confidence: null,
      notes: null,
      ...overrides,
    };
  }

  interface Example {
    id: string;
    title: string;
    /** What OCIANA's reading is, verbatim, so the chips can be checked against it. */
    translit: string;
    lede: string;
    tokens: TokenOut[];
    boxes: DraftBox[];
    /** Which chip the cursor is on. -1 for "the strip is finished". */
    cursor: number;
    /** Which token indices are boxed, for the chip state. */
    boxedTokens: number[];
    /** Slot indices that carry a mark on the stone. */
    marks: number[];
    takeaway: string;
    reference: string;
  }

  const EXAMPLES: Example[] = [
    {
      id: 'expected',
      title: 'The ordinary case: draw, and the chip under the cursor is assigned',
      translit: 'l rṯ',
      lede:
        'Two boxes drawn, two chips taken, cursor sitting on the third. Nothing was typed and no ' +
        'menu was opened — that is the whole loop.',
      tokens: [
        token(0, 'l', 'l', CLASS.l),
        token(1, 'r', 'r', CLASS.r, { wordIndex: 1 }),
        token(2, 'ṯ', 'ṯ', CLASS.ṯ, { wordIndex: 1 }),
      ],
      boxes: [box(0, 'l', 'l', CLASS.l), box(1, 'r', 'r', CLASS.r)],
      cursor: 2,
      boxedTokens: [0, 1],
      marks: [0, 1, 2],
      takeaway: 'Draw a box → the chip under the cursor is assigned → the cursor advances.',
      reference: '§3',
    },
    {
      id: 'uncertain',
      title: 'A {…} chip arrives pre-flagged uncertain',
      translit: 'bn {ṣ}',
      lede:
        'Braces are the scholar saying "I read a ṣ here, and I was not sure". The class is still ' +
        'ṣ — uncertainty is a flag on the box, never a class of its own.',
      tokens: [
        token(0, 'b', 'b', CLASS.b),
        token(1, 'n', 'n', CLASS.n),
        token(2, 'ṣ', 'ṣ', CLASS.ṣ, { uncertain: true, wordIndex: 1 }),
      ],
      boxes: [
        box(0, 'b', 'b', CLASS.b),
        box(1, 'n', 'n', CLASS.n),
        box(2, 'ṣ', 'ṣ', CLASS.ṣ, { uncertain: true }),
      ],
      cursor: 2,
      boxedTokens: [0, 1, 2],
      marks: [0, 1, 2],
      takeaway:
        'The flag rides along automatically. Toggle it yourself with U if the stone changes your mind.',
      reference: '§6.1',
    },
    {
      id: 'restored',
      title: 'A […] chip is editorially restored — usually nothing is carved there',
      translit: 'l [b]nt',
      lede:
        'Square brackets mean the editor supplied the letter; it is normally not visible on the ' +
        'stone. Box it only if you can actually see a glyph, and mark the chip not present (N) if ' +
        'you cannot.',
      tokens: [
        token(0, 'l', 'l', CLASS.l),
        token(1, 'b', 'b', CLASS.b, { restored: true, wordIndex: 1 }),
        token(2, 'n', 'n', CLASS.n, { wordIndex: 1 }),
        token(3, 't', 't', CLASS.t, { wordIndex: 1 }),
      ],
      boxes: [
        box(0, 'l', 'l', CLASS.l),
        box(1, 'b', 'b', CLASS.b, { restored: true }),
        box(2, 'n', 'n', CLASS.n),
      ],
      cursor: 3,
      boxedTokens: [0, 1, 2],
      marks: [0, 1, 2, 3],
      takeaway: 'Restored is a flag too. Seeing nothing there is a real answer, recorded with N.',
      reference: '§6.2, §6.7',
    },
    {
      id: 'unexpected',
      title: 'A glyph OCIANA does not have: an insertion',
      translit: 'l bn',
      lede:
        'The stone carries a fourth mark the reading does not enumerate. Arm insertion with I and ' +
        'box it: the disagreement is recorded, not reconciled away in either direction.',
      tokens: [
        token(0, 'l', 'l', CLASS.l),
        token(1, 'b', 'b', CLASS.b, { wordIndex: 1 }),
        token(2, 'n', 'n', CLASS.n, { wordIndex: 1 }),
      ],
      boxes: [
        box(0, 'l', 'l', CLASS.l),
        box(1, 'b', 'b', CLASS.b),
        box(2, 'n', 'n', CLASS.n),
        box(3, 'n', 'n', CLASS.n, { alignment: 'unexpected', expectedTokenIndex: null }),
      ],
      cursor: -1,
      boxedTokens: [0, 1, 2],
      marks: [0, 1, 2, 3],
      takeaway:
        'Never edit the transliteration to match the stone, and never skip a glyph to match the text.',
      reference: '§8',
    },
    {
      id: 'lacuna',
      title: 'One ---- chip covers a stretch of unknown length',
      translit: 'l ----',
      lede:
        'A dash run is one chip meaning "illegible stretch, length unknown" — not four letters. ' +
        'Three glyphs are legible inside this one: the first takes the chip, the other two become ' +
        'insertions. The cursor stays put until you move it with →.',
      tokens: [
        token(0, 'l', 'l', CLASS.l),
        token(1, 'illegible', 'illegible', CLASS.illegible, { wordIndex: 1 }),
      ],
      boxes: [
        box(0, 'l', 'l', CLASS.l),
        box(1, 'illegible', '—', CLASS.illegible),
        box(2, 'illegible', '—', CLASS.illegible, {
          alignment: 'unexpected',
          expectedTokenIndex: null,
        }),
        box(3, 'illegible', '—', CLASS.illegible, {
          alignment: 'unexpected',
          expectedTokenIndex: null,
        }),
      ],
      cursor: 1,
      boxedTokens: [0, 1],
      marks: [0, 1, 2, 3],
      takeaway:
        'One expected box plus two insertions is the correct record here, not an error to tidy up.',
      reference: '§6.3, §6.4',
    },
  ];

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  interface StageParams {
    marks: number[];
    boxes: DraftBox[];
  }

  /**
   * Draw one example on a real Konva stage.
   *
   * Same recipe as `ImageCanvas.redrawBoxes` and `StyleGallery`: the layer is in **image
   * coordinates**, and every visual attribute of a box comes from `boxStyle.ts`. Zoom is fixed
   * at 1 here (the zoom question is `/styles`'s job, not this page's), so `unit` is 1.
   */
  function stage(node: HTMLDivElement, params: StageParams) {
    // Wide enough for the right-most thing on it, mark or box, plus the left margin again.
    const rightEdge = Math.max(
      ...params.marks.map((index) => slot(index).x + slot(index).w),
      ...params.boxes.map((item) => item.bbox.x + item.bbox.w),
    );
    const width = Math.round(rightEdge + SLOT.x0);
    node.style.width = `${width}px`;
    node.style.height = `${STAGE_HEIGHT}px`;

    const konvaStage = new Konva.Stage({ container: node, width, height: STAGE_HEIGHT });
    // Non-listening: this is a diagram, not a canvas. Nothing here is draggable and nothing
    // should be mistaken for the real workspace.
    const rock = new Konva.Layer({ listening: false });
    const overlay = new Konva.Layer({ listening: false });
    konvaStage.add(rock, overlay);

    rock.add(new Konva.Rect({ x: 0, y: 0, width, height: STAGE_HEIGHT, fill: ROCK }));
    // A couple of fixed shading bands so the "stone" is not a flat swatch, which would make the
    // box fills read as the subject rather than as an overlay.
    rock.add(
      new Konva.Rect({ x: 0, y: 0, width, height: 26, fill: ROCK_SHADE }),
      new Konva.Rect({ x: 0, y: STAGE_HEIGHT - 18, width, height: 18, fill: ROCK_SHADE }),
    );

    for (const index of params.marks) {
      const mark = slot(index);
      for (const polyline of MARKS[index % MARKS.length]!) {
        const points: number[] = [];
        for (let i = 0; i < polyline.length; i += 2) {
          points.push(mark.x + polyline[i]! * mark.w, mark.y + polyline[i + 1]! * mark.h);
        }
        rock.add(
          new Konva.Line({
            points,
            stroke: INCISION,
            strokeWidth: 5,
            lineCap: 'round',
            lineJoin: 'round',
          }),
        );
      }
    }

    for (const item of params.boxes) {
      overlay.add(
        new Konva.Rect({
          x: item.bbox.x,
          y: item.bbox.y,
          width: item.bbox.w,
          height: item.bbox.h,
          ...boxRectAttrs(item),
        }),
        new Konva.Text({
          x: item.bbox.x,
          y: item.bbox.y - LABEL_OFFSET,
          ...boxLabelAttrs(item, 1),
        }),
      );
    }

    rock.draw();
    overlay.draw();

    return {
      destroy(): void {
        konvaStage.destroy();
      },
    };
  }

  function chipState(example: Example, index: number): 'boxed' | 'open' {
    return example.boxedTokens.includes(index) ? 'boxed' : 'open';
  }

  function chipDescription(example: Example, tokenItem: TokenOut): string {
    const parts = [`letter ${tokenItem.index + 1} of ${example.tokens.length}`];
    if (tokenItem.uncertain) parts.push('marked uncertain by the editor');
    if (tokenItem.restored) parts.push('editorially restored');
    if (isLacunaToken(tokenItem)) parts.push('illegible stretch of unknown length');
    parts.push(chipState(example, tokenItem.index) === 'boxed' ? 'boxed' : 'not yet resolved');
    if (example.cursor === tokenItem.index) parts.push('cursor is here');
    return parts.join(', ');
  }
</script>

<section class="panel col" aria-labelledby="worked-examples-heading">
  <h2 id="worked-examples-heading">Worked examples</h2>
  <p class="small muted intro">
    Every rectangle below is a real Konva stage drawn through
    <span class="mono">src/lib/boxStyle.ts</span> — the same module the annotation canvas uses, so
    an example cannot show a border style the tool does not draw. The marks on the "stone" are
    <strong>abstract placeholder shapes, not Hismaic letterforms</strong>: this page teaches the
    workflow, and drawing an invented glyph would be inventing evidence. There is no photograph
    here on purpose — imagery is served as short-lived presigned URLs, and this page has to work
    with no bucket configured.
  </p>

  {#each EXAMPLES as example (example.id)}
    <article class="example col" data-example={example.id}>
      <header class="col tight">
        <h3>{example.title}</h3>
        <p class="small muted">
          OCIANA reads <span class="glyph translit">{example.translit}</span>
          · guidelines <span class="mono">{example.reference}</span>
        </p>
      </header>

      <p class="small lede">{example.lede}</p>

      <div class="figure row wrap">
        <div class="stage-host" use:stage={{ marks: example.marks, boxes: example.boxes }}></div>

        <div class="strip col tight" aria-label="Expected letter chips for this example">
          <span class="small muted">Chip strip</span>
          <div class="chips row wrap">
            {#each example.tokens as chip (chip.index)}
              <span
                class="chip {chipState(example, chip.index)}"
                class:cursor={example.cursor === chip.index}
                class:uncertain={chip.uncertain}
                class:restored={chip.restored}
                class:lacuna={isLacunaToken(chip)}
                title={chipDescription(example, chip)}
              >
                <span class="index mono">{chip.index + 1}</span>
                <span class="glyph letter">{tokenLabel(chip)}</span>
                <span class="marks">
                  {#if chip.uncertain}<span title="uncertain reading">?</span>{/if}
                  {#if chip.restored}<span title="editorially restored">[ ]</span>{/if}
                  {#if chipState(example, chip.index) === 'boxed'}<span title="boxed">✓</span>{/if}
                </span>
              </span>
            {/each}
          </div>
          {#if example.cursor >= 0}
            <span class="small muted">
              Cursor on chip {example.cursor + 1}{#if isLacunaToken(example.tokens[example.cursor])}
                — and it stays there: nobody knows how many glyphs a lacuna holds{/if}.
            </span>
          {:else}
            <span class="small muted">Every chip resolved; the extra box is an insertion.</span>
          {/if}
        </div>
      </div>

      <p class="small takeaway"><strong>Takeaway.</strong> {example.takeaway}</p>
    </article>
  {/each}
</section>

<style>
  .intro {
    margin: 0;
    max-width: 78ch;
  }

  .example {
    border-top: 1px solid var(--line);
    padding-top: 12px;
    gap: 8px;
  }

  .tight {
    gap: 2px;
  }

  .lede,
  .takeaway {
    margin: 0;
    max-width: 78ch;
  }

  .takeaway {
    color: var(--text-dim);
  }

  .translit {
    font-size: 1rem;
    color: var(--text);
  }

  .figure {
    gap: 18px;
    align-items: flex-start;
  }

  .stage-host {
    border: 1px solid var(--line);
    border-radius: 6px;
    overflow: hidden;
    flex: none;
  }

  .strip {
    min-width: 200px;
  }

  /* Presentational only. The authoritative chip strip is `TokenStrip.svelte`, which is bound to
     the session store and cannot be mounted here without an open image; these are static
     look-alikes carrying the same three signals (border style, badge, colour). */
  .chip {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    min-width: 38px;
    padding: 2px 6px 3px;
    background: var(--bg-raised);
    border: 2px solid var(--line);
    border-radius: 6px;
    line-height: 1.15;
  }

  .chip .index {
    color: var(--text-dim);
    font-size: var(--text-xs);
  }

  .chip .letter {
    font-size: 1.3rem;
  }

  .chip .marks {
    min-height: 1em;
    font-size: var(--text-xs);
    color: var(--text-dim);
    letter-spacing: 0.06em;
  }

  .chip.uncertain {
    border-style: dashed;
    border-color: var(--uncertain);
  }

  .chip.restored {
    border-style: dotted;
    border-color: var(--restored);
  }

  .chip.lacuna .letter {
    color: var(--text-dim);
  }

  .chip.boxed {
    background: #1d2b22;
    border-color: var(--ok);
  }

  .chip.cursor {
    outline: 3px solid var(--accent);
    outline-offset: 1px;
  }
</style>
